import React from "react";
import { connect } from "react-redux";
import agent from "../agent";
import {
  ASYNC_START,
  LOAD_NOTIFICATIONS,
  CREATE_NOTIFICATION,
  SEND_NOTIFICATION,
  DELETE_NOTIFICATION,
  LOAD_RECIPIENTS,
} from "../constants/actionTypes";
import ListErrors from "./ListErrors";
import ListSuccess from "./ListSuccess";
import { ListPagination } from "../widgets/helpers/ListPagination";

import { AsideDefault } from "../widgets/components/aside/AsideDefault";
import { Footer } from "../widgets/components/Footer";
import { HeaderWrapper } from "../widgets/components/header/HeaderWrapper";
import { Toolbar } from "../widgets/components/toolbar/Toolbar";
import { ScrollTop } from "../widgets/components/ScrollTop";
import { Content } from "../widgets/components/Content";

const mapStateToProps = (state) => ({
  ...state.notifications,
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  loadNotifications: (page, limit, search) =>
    dispatch({
      type: LOAD_NOTIFICATIONS,
      payload: agent.Notifications.list(page, limit, search),
    }),
  onLoadRecipients: () =>
    dispatch({
      type: LOAD_RECIPIENTS,
      payload: agent.Notifications.getRecipients(),
    }),
  onCreate: (notificationData) =>
    dispatch({
      type: CREATE_NOTIFICATION,
      payload: agent.Notifications.create(notificationData),
    }),
  onSend: (id) =>
    dispatch({
      type: SEND_NOTIFICATION,
      payload: agent.Notifications.send(id),
    }),
  onDelete: (id) =>
    dispatch({
      type: DELETE_NOTIFICATION,
      payload: agent.Notifications.delete(id),
    }),
});

class AdminNotifications extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCreateModal: false,
      selectedNotification: null,
      showSendModal: false,
      formData: {
        title: "",
        content: "",
        recipient_type: "all",
        user_ids: [],
        group_ids: [],
      },
      selectedUsers: [],
      selectedGroups: [],
      errors: null,
    };
  }

  componentDidMount() {
    this.props.loadNotifications(
      this.props.currentPage || 1,
      this.props.currentLimit || 15,
      this.props.currentSearch || ""
    );
    this.props.onLoadRecipients();
  }

  componentDidUpdate(prevProps) {
    // Close modal and reset state when notification is successfully created
    if (
      !prevProps.successMessage &&
      this.props.successMessage &&
      this.state.showCreateModal
    ) {
      this.setState({
        showCreateModal: false,
        formData: {
          title: "",
          content: "",
          recipient_type: "all",
          user_ids: [],
          group_ids: [],
        },
        selectedUsers: [],
        selectedGroups: [],
        errors: null,
      });
    }

    // Close send modal after successful send and reload notifications
    if (
      !prevProps.successMessage &&
      this.props.successMessage &&
      this.state.showSendModal
    ) {
      this.setState({
        showSendModal: false,
        selectedNotification: null,
      });
      // Reload notifications to show updated status
      this.props.loadNotifications(
        this.props.currentPage || 1,
        this.props.currentLimit || 15,
        this.props.currentSearch || ""
      );
    }

    // Reload notifications after successful delete
    if (
      !prevProps.successMessage &&
      this.props.successMessage &&
      this.props.successMessage.includes("deleted")
    ) {
      // Reload notifications to reflect deletion
      this.props.loadNotifications(
        this.props.currentPage || 1,
        this.props.currentLimit || 15,
        this.props.currentSearch || ""
      );
    }
  }

  handleCreateNew = () => {
    this.setState({
      showCreateModal: true,
      formData: {
        title: "",
        content: "",
        recipient_type: "all",
        user_ids: [],
        group_ids: [],
      },
      selectedUsers: [],
      selectedGroups: [],
      errors: null,
    });
  };

  handleCloseModal = () => {
    this.setState({
      showCreateModal: false,
      showSendModal: false,
      selectedNotification: null,
    });
  };

  handleInputChange = (field, value) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: value,
      },
    });
  };

  handleRecipientTypeChange = (type) => {
    this.setState({
      formData: {
        ...this.state.formData,
        recipient_type: type,
        user_ids: [],
        group_ids: [],
      },
      selectedUsers: [],
      selectedGroups: [],
    });
  };

  handleUserSelection = (userId) => {
    const { selectedUsers } = this.state;
    const newSelectedUsers = selectedUsers.includes(userId)
      ? selectedUsers.filter((id) => id !== userId)
      : [...selectedUsers, userId];

    this.setState({
      selectedUsers: newSelectedUsers,
      formData: {
        ...this.state.formData,
        user_ids: newSelectedUsers,
      },
    });
  };

  handleGroupSelection = (groupId) => {
    const { selectedGroups } = this.state;
    const newSelectedGroups = selectedGroups.includes(groupId)
      ? selectedGroups.filter((id) => id !== groupId)
      : [...selectedGroups, groupId];

    this.setState({
      selectedGroups: newSelectedGroups,
      formData: {
        ...this.state.formData,
        group_ids: newSelectedGroups,
      },
    });
  };

  handleCreateSubmit = () => {
    const { formData } = this.state;
    let errors = [];

    if (!formData.title.trim()) {
      errors.push("Title is required");
    }
    if (!formData.content.trim()) {
      errors.push("Content is required");
    }
    if (
      formData.recipient_type === "selected_users" &&
      formData.user_ids.length === 0
    ) {
      errors.push("Please select at least one user");
    }
    if (
      formData.recipient_type === "selected_groups" &&
      formData.group_ids.length === 0
    ) {
      errors.push("Please select at least one group");
    }

    if (errors.length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ errors: null });
    this.props.onCreate(formData);
  };

  handleSendNotification = (notification) => {
    this.setState({
      selectedNotification: notification,
      showSendModal: true,
    });
  };

  handleConfirmSend = () => {
    if (this.state.selectedNotification) {
      this.props.onSend(this.state.selectedNotification.id);
      this.handleCloseModal();
    }
  };

  getRecipientCount = () => {
    const { formData } = this.state;
    const { users, groups } = this.props;

    if (formData.recipient_type === "all") {
      return users ? users.length : "Loading...";
    } else if (formData.recipient_type === "selected_users") {
      return formData.user_ids.length;
    } else if (formData.recipient_type === "selected_groups") {
      if (!users || !groups || formData.group_ids.length === 0) return 0;

      // Count users in selected roles
      let totalUsers = 0;
      const selectedRoles = formData.group_ids;

      // Create a Set to avoid counting duplicate users if they somehow have multiple roles
      const uniqueUserIds = new Set();

      users.forEach((user) => {
        // Note: This assumes user has a 'role' property. If not, we'll need to adjust.
        if (selectedRoles.includes(user.role)) {
          uniqueUserIds.add(user.id);
        }
      });

      return uniqueUserIds.size;
    }
    return 0;
  };

  formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  clickPagination = (pageNumber) => {
    this.props.loadNotifications(
      pageNumber,
      this.props.currentLimit || 15,
      this.props.currentSearch || ""
    );
  };

  clickLimit = (limitNumber) => {
    this.props.loadNotifications(
      this.props.currentPage || 1,
      limitNumber,
      this.props.currentSearch || ""
    );
  };

  render() {
    const {
      notifications,
      users,
      groups,
      successMessage,
      errorMessage,
      inProgress,
    } = this.props;

    const {
      showCreateModal,
      showSendModal,
      formData,
      selectedUsers,
      selectedGroups,
      errors,
      selectedNotification,
    } = this.state;

    return (
      <>
        <div className="d-flex flex-column flex-root">
          <div className="page d-flex flex-row flex-column-fluid">
            <AsideDefault />
            <div
              className="wrapper d-flex flex-column flex-row-fluid"
              id="kt_wrapper"
            >
              <HeaderWrapper
                title="Notifications Management"
                currentUser={this.props.currentUser}
              />
              <Toolbar />
              <div
                id="kt_content"
                className="content d-flex flex-column flex-column-fluid"
              >
                <Content>
                  <div className="container-fluid">
                    <div className="card">
                      <div className="card-header">
                        <div className="card-title">
                          <h3>Notifications Management</h3>
                        </div>
                        <div className="card-toolbar">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={this.handleCreateNew}
                          >
                            Create Notification
                          </button>
                        </div>
                      </div>

                      <div className="card-body">
                        {successMessage && (
                          <ListSuccess errors={[successMessage]} />
                        )}
                        {errorMessage && <ListErrors errors={[errorMessage]} />}

                        <div className="table-responsive">
                          <table className="table table-striped table-row-bordered gy-5 gs-7">
                            <thead>
                              <tr className="fw-bold fs-6 text-gray-800">
                                <th>Title</th>
                                <th>Content Preview</th>
                                <th>Recipients</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {notifications && notifications.length > 0 ? (
                                notifications.map((notification) => (
                                  <tr key={notification.id}>
                                    <td>{notification.title}</td>
                                    <td>
                                      {notification.content.length > 50
                                        ? notification.content.substring(
                                            0,
                                            50
                                          ) + "..."
                                        : notification.content}
                                    </td>
                                    <td>
                                      {notification.total_recipients} recipients
                                      {notification.status === "sent" && (
                                        <span className="text-muted">
                                          {" "}
                                          ({notification.sent_count} sent)
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          notification.status === "sent"
                                            ? "badge-success"
                                            : notification.status === "failed"
                                            ? "badge-danger"
                                            : "badge-warning"
                                        }`}
                                      >
                                        {notification.status.toUpperCase()}
                                      </span>
                                    </td>
                                    <td>
                                      {this.formatDateTime(
                                        notification.created_at
                                      )}
                                    </td>
                                    <td>
                                      {notification.status === "draft" && (
                                        <>
                                          <button
                                            className="btn btn-sm btn-success me-2"
                                            onClick={() =>
                                              this.handleSendNotification(
                                                notification
                                              )
                                            }
                                          >
                                            Send
                                          </button>
                                          <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                              this.props.onDelete(
                                                notification.id
                                              )
                                            }
                                          >
                                            Delete
                                          </button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    {inProgress
                                      ? "Loading notifications..."
                                      : "No notifications found"}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <ListPagination
                          pagination={this.props.pagination}
                          results={this.props.notifications}
                          clickPagination={this.clickPagination}
                          clickLimit={this.clickLimit}
                          isLoading={this.props.inProgress}
                        />
                      </div>
                    </div>
                  </div>
                </Content>
              </div>
              <Footer />
            </div>
          </div>
        </div>
        <ScrollTop />

        {/* Create Notification Modal */}
        {showCreateModal && (
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create New Notification</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={this.handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {errors && <ListErrors errors={errors} />}

                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) =>
                        this.handleInputChange("title", e.target.value)
                      }
                      maxLength={255}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Content</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={formData.content}
                      onChange={(e) =>
                        this.handleInputChange("content", e.target.value)
                      }
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Recipients</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipientType"
                        checked={formData.recipient_type === "all"}
                        onChange={() => this.handleRecipientTypeChange("all")}
                      />
                      <label className="form-check-label">
                        All Active Users
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipientType"
                        checked={formData.recipient_type === "selected_users"}
                        onChange={() =>
                          this.handleRecipientTypeChange("selected_users")
                        }
                      />
                      <label className="form-check-label">Selected Users</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="recipientType"
                        checked={formData.recipient_type === "selected_groups"}
                        onChange={() =>
                          this.handleRecipientTypeChange("selected_groups")
                        }
                      />
                      <label className="form-check-label">
                        Selected Groups
                      </label>
                    </div>
                  </div>

                  {formData.recipient_type === "selected_users" && users && (
                    <div className="mb-3">
                      <label className="form-label">Select Users:</label>
                      <div
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #ddd",
                          padding: "10px",
                        }}
                      >
                        {users.map((user) => (
                          <div key={user.id} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => this.handleUserSelection(user.id)}
                            />
                            <label className="form-check-label">
                              {user.firstname} {user.lastname} ({user.email})
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.recipient_type === "selected_groups" && groups && (
                    <div className="mb-3">
                      <label className="form-label">Select Groups:</label>
                      <div
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #ddd",
                          padding: "10px",
                        }}
                      >
                        {groups.map((group) => (
                          <div key={group.id} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedGroups.includes(group.id)}
                              onChange={() =>
                                this.handleGroupSelection(group.id)
                              }
                            />
                            <label className="form-check-label">
                              {group.name}
                              {group.description && (
                                <span className="text-muted">
                                  {" "}
                                  - {group.description}
                                </span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="alert alert-info">
                    <strong>Preview:</strong> This notification will be sent to{" "}
                    {this.getRecipientCount()} recipients.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={this.handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={this.handleCreateSubmit}
                    disabled={inProgress}
                  >
                    {inProgress ? "Creating..." : "Create Notification"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Confirmation Modal */}
        {showSendModal && selectedNotification && (
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Send Notification</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={this.handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to send this notification?</p>
                  <div className="alert alert-warning">
                    <strong>Title:</strong> {selectedNotification.title}
                    <br />
                    <strong>Recipients:</strong>{" "}
                    {selectedNotification.total_recipients} users
                    <br />
                    <strong>Note:</strong> This action cannot be undone.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={this.handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={this.handleConfirmSend}
                    disabled={inProgress}
                  >
                    {inProgress ? "Sending..." : "Send Notification"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal backdrop */}
        {(showCreateModal || showSendModal) && (
          <div className="modal-backdrop fade show"></div>
        )}
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminNotifications);
